import { WorkItem, JobWithNotes, Appointment } from '../workItemTypes';

/**
 * Get the assignee for a work item by looking up the corresponding job or appointment
 */
export const getWorkItemAssignee = (workItem: WorkItem, jobs: JobWithNotes[], appointments: Appointment[]): string => {
  if (workItem.type === 'job') {
    const job = jobs.find(j => j.id === workItem.id);
    return job?.assignedUserId || 'Unassigned';
  } else if (workItem.type === 'appointment') {
    const appointment = appointments.find(a => a.id === workItem.id);
    return appointment?.assignedUsers || 'Unassigned';
  }
  return 'Unassigned';
};

/**
 * Get assignee from a job or appointment directly
 */
export const getAssigneeFromWorkItemData = (item: JobWithNotes | Appointment): string => {
  if ('assignedUserId' in item) {
    // This is a Job
    return item.assignedUserId || 'Unassigned';
  } else if ('assignedUsers' in item) {
    // This is an Appointment
    return item.assignedUsers || 'Unassigned';
  }
  return 'Unassigned';
};

/**
 * Add assignee information to work items
 */
export const enrichWorkItemsWithAssignee = (workItems: WorkItem[], jobs: JobWithNotes[], appointments: Appointment[]): (WorkItem & { assignedTo: string })[] => {
  return workItems.map(workItem => ({
    ...workItem,
    assignedTo: getWorkItemAssignee(workItem, jobs, appointments)
  }));
};

/**
 * Group work items by their assigned user
 */
export const groupWorkItemsByAssignee = (
  workItems: WorkItem[], 
  jobs: JobWithNotes[], 
  appointments: Appointment[]
): Record<string, (WorkItem & { assignedTo: string })[]> => {
  const enrichedWorkItems = enrichWorkItemsWithAssignee(workItems, jobs, appointments);
  const grouped: Record<string, (WorkItem & { assignedTo: string })[]> = {};
  
  enrichedWorkItems.forEach(workItem => {
    const assignee = workItem.assignedTo;
    if (!grouped[assignee]) {
      grouped[assignee] = [];
    }
    grouped[assignee].push(workItem);
  });
  
  // Sort work items within each group by displayIndex
  Object.keys(grouped).forEach(assignee => {
    grouped[assignee].sort((a, b) => {
      // Handle displayIndex as number
      const aIndex = Number(a.displayIndex || 0);
      const bIndex = Number(b.displayIndex || 0);
      return aIndex - bIndex;
    });
  });
  
  return grouped;
};

/**
 * Sort assignees with current user first, then alphabetically
 */
export const sortAssignees = (assignees: string[], currentUser?: string): string[] => {
  return assignees.sort((a, b) => {
    // Current user first - more robust matching
    if (currentUser) {
      const currentUserFirstName = currentUser.split(' ')[0].toLowerCase();
      const aLower = a.toLowerCase();
      const bLower = b.toLowerCase();
      
      const aIsCurrentUser = aLower.includes(currentUserFirstName) || aLower === currentUser.toLowerCase();
      const bIsCurrentUser = bLower.includes(currentUserFirstName) || bLower === currentUser.toLowerCase();
      
      if (aIsCurrentUser && !bIsCurrentUser) return -1;
      if (!aIsCurrentUser && bIsCurrentUser) return 1;
    }
    
    // Then alphabetically
    return a.localeCompare(b);
  });
};

/**
 * Get available technicians from work items
 */
export const getAvailableTechnicians = (jobs: JobWithNotes[], appointments: Appointment[]): string[] => {
  const technicians = new Set<string>();
  
  jobs.forEach(job => {
    if (job.assignedUserId && job.assignedUserId.trim()) {
      technicians.add(job.assignedUserId);
    }
  });
  
  appointments.forEach(appointment => {
    if (appointment.assignedUsers && appointment.assignedUsers.trim()) {
      technicians.add(appointment.assignedUsers);
    }
  });
  
  return Array.from(technicians).sort();
};

/**
 * Normalise any status string to a kebab-case slug.
 * Matches the Python `normalize_status` in core/utils.py.
 *
 *   "Waiting for Parts" -> "waiting-for-parts"
 *   "in-progress"       -> "in-progress"   (already normalised)
 */
export const normalizeStatus = (status: string): string =>
  status.trim().replace(/\s+/g, '-').toLowerCase();

/**
 * Format work item status for display (kebab-case -> Title Case)
 */
export const formatStatusDisplay = (status: string): string => {
  return status
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Get CSS class for work item status.
 * Accepts any format (title-case or kebab-case) and returns a CSS class.
 */
export const getStatusClass = (status: string): string => {
  const slug = normalizeStatus(status);
  return `status-${slug}`;
};

/**
 * Format timestamp for display
 */
export const formatTimestamp = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return timestamp;
  }
};

/**
 * Smart date formatting:
 * - Today → "Today 13:00"
 * - Within last 7 days → "Wed 13:00"
 * - Older → relative like "2 weeks ago"
 */
export const formatSmartDate = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const time = date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });

    const isToday =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();

    if (isToday) return `Today ${time}`;
    if (diffDays < 7) {
      const dayName = date.toLocaleDateString('en-AU', { weekday: 'short' });
      return `${dayName} ${time}`;
    }
    if (diffDays < 14) return '1 week ago';
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 60) return '1 month ago';
    return `${Math.floor(diffDays / 30)} months ago`;
  } catch {
    return dateStr;
  }
};

/**
 * Format a phone call tooltip with full date detail.
 * - Inbound → "Customer called TechSolvers Wednesday 13th May 13:00"
 * - Outbound → "TechSolvers called customer Wednesday 13th May 13:00"
 */
export const formatPhoneCallTooltip = (direction: 'inbound' | 'outbound', dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const dayName = date.toLocaleDateString('en-AU', { weekday: 'long' });
    const day = date.getDate();
    const month = date.toLocaleDateString('en-AU', { month: 'long' });
    const time = date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });

    // Ordinal suffix
    const suffix =
      day % 10 === 1 && day !== 11 ? 'st' :
      day % 10 === 2 && day !== 12 ? 'nd' :
      day % 10 === 3 && day !== 13 ? 'rd' : 'th';

    const dateFormatted = `${dayName} ${day}${suffix} ${month} ${time}`;

    return direction === 'inbound'
      ? `Customer called TechSolvers ${dateFormatted}`
      : `TechSolvers called customer ${dateFormatted}`;
  } catch {
    return '';
  }
};

/**
 * Format customer display as "Business Name - Full Name"
 */
export const formatCustomerDisplay = (workItem: WorkItem): string => {
  const businessName = workItem.customerBusinessName || '';
  const fullName = workItem.customerName || '';
  
  // If both exist and are different, show "Business - Customer"
  if (businessName && fullName && businessName !== fullName) {
    return `${businessName} - ${fullName}`;
  }
  
  // If only one exists, show that one
  if (fullName) {
    return fullName;
  }
  
  if (businessName) {
    return businessName;
  }
  
  // Fallback
  return 'Unknown Customer';
};

/**
 * Short customer display for compact rows: the business name when present,
 * otherwise the person's full name. Use `formatCustomerDisplay` for the full
 * "Business - Full Name" form (e.g. as hover/title text).
 */
export const formatCustomerShort = (workItem: WorkItem): string => {
  const businessName = workItem.customerBusinessName || '';
  const fullName = workItem.customerName || '';

  if (businessName) {
    return businessName;
  }

  if (fullName) {
    return fullName;
  }

  return 'Unknown Customer';
};

/**
 * Check if a date string represents today's date.
 */
export const isToday = (dateStr?: string): boolean => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
};

/**
 * Check if a date string represents yesterday's date.
 */
export const isYesterday = (dateStr?: string): boolean => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate()
  );
};

/**
 * Check if a date string is in the future (after today).
 */
export const isFutureDate = (dateStr?: string): boolean => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  // Zero out time for date-only comparison
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return dateStart.getTime() > todayStart.getTime();
};

/**
 * Check if an appointment's scheduled time is in the past (overdue).
 */
export const isPastTime = (dateStr?: string): boolean => {
  if (!dateStr) return false;
  return new Date(dateStr).getTime() < Date.now();
};

/**
 * Sort work items for dashboard display within a technician group.
 *
 * Three tiers:
 * 1. Today's appointments — pinned to top, sorted by startAt time ascending
 * 2. Jobs (and items without appointment dates) — middle, sorted by displayIndex
 * 3. Future appointments — pinned to bottom, sorted by startAt date ascending
 *
 * Appointment retention rules:
 * - Scheduled/Overdue: visible on scheduled day and future
 * - Completed: remains until end of scheduled day
 * - Cancelled: remains until end of scheduled day
 * - No Show: remains until end of the following day
 */
export const sortWorkItemsForDashboard = (items: WorkItem[], showResolvedToday = false): WorkItem[] => {
  const todayAppointments: WorkItem[] = [];
  const resolvedToday: WorkItem[] = [];
  const jobs: WorkItem[] = [];
  const futureAppointments: WorkItem[] = [];

  for (const item of items) {
    const isAppointmentType = item.type === 'appointment' || item.type === 'job-appointment';
    const appointmentStartAt = item.startAt;

    if (!isAppointmentType) {
      if (showResolvedToday && ['resolved', 'closed'].includes((item.status ?? '').toLowerCase())) {
        resolvedToday.push(item);
      } else {
        jobs.push(item);
      }
      continue;
    }

    const status = item.status || item.appointmentStatus || '';
    const scheduledToday = isToday(appointmentStartAt);
    const scheduledYesterday = isYesterday(appointmentStartAt);
    const scheduledFuture = isFutureDate(appointmentStartAt);

    if (scheduledToday) {
      // All appointments on their scheduled day are visible
      todayAppointments.push(item);
    } else if (scheduledYesterday && status === 'no-show') {
      // No Show remains until end of the following day
      todayAppointments.push(item);
    } else if (scheduledFuture) {
      futureAppointments.push(item);
    } else {
      // Past appointments (completed, cancelled, or older no-shows) — drop into jobs bucket
      jobs.push(item);
    }
  }

  // Today's appointments sorted by start time
  todayAppointments.sort((a, b) => {
    const aTime = a.startAt ? new Date(a.startAt).getTime() : 0;
    const bTime = b.startAt ? new Date(b.startAt).getTime() : 0;
    return aTime - bTime;
  });

  // Resolved items sorted by updated time (most recent first)
  resolvedToday.sort((a, b) => {
    const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    return bTime - aTime;
  });

  // Jobs sorted by displayIndex (existing behavior)
  jobs.sort((a, b) => (Number(a.displayIndex) || 0) - (Number(b.displayIndex) || 0));

  // Future appointments sorted by start date
  futureAppointments.sort((a, b) => {
    const aTime = a.startAt ? new Date(a.startAt).getTime() : 0;
    const bTime = b.startAt ? new Date(b.startAt).getTime() : 0;
    return aTime - bTime;
  });

  return [...resolvedToday, ...todayAppointments, ...jobs, ...futureAppointments];
};

/**
 * Build a billing summary string for display in the accordion row.
 *
 * Examples:
 *  - "No line items"
 *  - "$180.00 (Not invoiced)"
 *  - "$180.00 (Invoiced, paid)"
 *  - "$90.00 (Paid), $180.00 (Not invoiced)"
 */
export interface BillingSummary {
  /** Compact display string, e.g. "$90.00 (Paid), $185.00 (Invoiced, unpaid)" */
  label: string;
  /** Structured segments behind `label` — one per invoice / uninvoiced total. */
  segments: { amount: string; label: string }[];
  /** Detailed line-item breakdown for tooltip / hover display */
  details: string[];
}

export const getBillingSummary = (workItem: WorkItem): BillingSummary => {
  const hasRsItems = workItem.rsLineItems && workItem.rsLineItems.length > 0;
  const hasInvoices = workItem.invoices && workItem.invoices.length > 0;
  if (!hasRsItems && !hasInvoices) {
    return { label: 'No line items', segments: [], details: [] };
  }

  const fmt = (n: number) => `$${n.toFixed(2)}`;
  const segments: { amount: string; label: string }[] = [];
  const details: string[] = [];

  // Invoice segments from API-provided summaries
  if (hasInvoices) {
    for (const inv of workItem.invoices!) {
      const total = parseFloat(inv.total) || 0;
      const label = inv.is_paid ? 'Paid' : 'Invoiced, unpaid';
      segments.push({ amount: fmt(total), label });

      // Line item details for tooltip
      if (inv.line_items && inv.line_items.length > 0) {
        details.push(`Invoice #${inv.number} (${label}):`);
        for (const li of inv.line_items) {
          const qty = parseFloat(String(li.quantity)) || 0;
          const price = parseFloat(String(li.price)) || 0;
          const lineTotal = qty * price;
          const sku = li.description || li.name;
          details.push(`  ${sku} × ${qty} = ${fmt(lineTotal)}`);
        }
      }
    }
  }

  // Uninvoiced total from RS line items
  if (hasRsItems) {
    const nonChargeableSet = new Set<number>();
    if (workItem.lineItemOverrides) {
      for (const o of workItem.lineItemOverrides) {
        if (o.nonChargeable) nonChargeableSet.add(o.rsLineItemId);
      }
    }
    const uninvoiced = workItem.rsLineItems!.filter(
      li => !li.converted && !nonChargeableSet.has(li.id),
    );
    if (uninvoiced.length > 0) {
      const total = uninvoiced.reduce(
        (sum, li) => sum + li.price_retail * (parseFloat(li.quantity) || 0),
        0,
      );
      segments.push({ amount: fmt(total), label: 'Not invoiced' });

      details.push('Not invoiced:');
      for (const li of uninvoiced) {
        const qty = parseFloat(li.quantity) || 0;
        const lineTotal = qty * li.price_retail;
        details.push(`  ${li.name} × ${qty} = ${fmt(lineTotal)}`);
      }
    }
  }

  return {
    label: segments.length > 0
      ? segments.map(s => `${s.amount} (${s.label})`).join(', ')
      : 'No line items',
    segments,
    details,
  };
};

export const getRsLineItemsChargeableTotal = (workItem: WorkItem): number | null => {
  if (!workItem.rsLineItems || workItem.rsLineItems.length === 0) return null;
  const nonChargeableSet = new Set<number>();
  if (workItem.lineItemOverrides) {
    for (const o of workItem.lineItemOverrides) {
      if (o.nonChargeable) nonChargeableSet.add(o.rsLineItemId);
    }
  }
  return workItem.rsLineItems
    .filter((li) => !nonChargeableSet.has(li.id))
    .reduce((sum, li) => sum + li.price_retail * (parseFloat(li.quantity) || 0), 0);
};

/**
 * Format appointment date and time for display
 */
export const formatAppointmentDateTime = (startAt?: string, endAt?: string): string => {
  if (!startAt) {
    return 'No date set';
  }
  
  try {
    const start = new Date(startAt);
    const startFormatted = start.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    if (endAt) {
      const end = new Date(endAt);
      const endFormatted = end.toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      return `${startFormatted} - ${endFormatted}`;
    }
    
    return startFormatted;
  } catch (error) {
    return startAt;
  }
};